import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Divider, Chip } from '@mui/material';
import { DeleteIcon } from '../../components/buttons/buttons';
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams, GridRowParams, GridColumnVisibilityModel } from '@mui/x-data-grid';
import StandardDialog from '../../components/dialogs/base/standardDialog';
import Form, { FormData, FormRef } from './components/form';
import { toast } from '../../utils/toast';
import { useDeleteDialog } from '../../hooks';
import Header from './components/header';
import { apiDelete, apiGet, apiPost, showErrorToast } from '../../services/apiClient';
import { toDateInputValue } from '../../utils/misc';
import { useTranslation } from 'react-i18next';
import { OfferQuery, OfferQueryStrategy } from '../../models/offerQuery';

const OfferQueryPage: React.FC = () => {
  const { t } = useTranslation();

  const [offerQueries, setOfferQueries] = useState<OfferQuery[]>([]);
  const [query, setQuery] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    active: true
  } as FormData);
  const [action, setAction] = useState<'create' | 'update'>('create');
  const { DeleteModal, confirmDelete } = useDeleteDialog();
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 30
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({ id: false });
  const formRef = useRef<FormRef>(null);

  const fetchOfferQueries = useCallback(
    async () => {
      try {
        setLoading(true);
        const { page, pageSize } = paginationModel;
        const resp = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/offer_queries`, {
          ...query,
          offset: page * pageSize,
          limit: pageSize,
        });
        const { result, count } = await resp.json();
        result.forEach((item: FormData) => {
          item.startCountry = item.startArea?.country;
          item.startCity = item.startArea?.city;
          item.startPostalCode = item.startArea?.postalCode;
          item.endCountry = item.endArea?.country;
          item.endCity = item.endArea?.city;
          item.endPostalCode = item.endArea?.postalCode;
          item.vehicleTypes = item.vehicleProperties?.type;
          item.vehicleBodies = item.vehicleProperties?.body;
          item.vehicleBodyProperties = item.vehicleProperties?.bodyProperty;
          item.vehicleEquipments = item.vehicleProperties?.equipment;
          item.vehicleLoadSecurities = item.vehicleProperties?.loadSecuring;
          item.vehicleSwappableBodyTypes = item.vehicleProperties?.swapBody;
          item.firstSentNotBefore = item.firstSentNotBefore ? toDateInputValue(item.firstSentNotBefore) : '';
          item.lastSentNotAfter = item.lastSentNotAfter ? toDateInputValue(item.lastSentNotAfter) : '';
        });
        setOfferQueries(result);
        setRowCount(count);
      } catch (error) {
        showErrorToast(error, t('Failed to fetch offer queries'));
      } finally {
        setLoading(false);
      }
    },
    [query, paginationModel, t]
  );

  useEffect(() => {
    fetchOfferQueries();
  }, [fetchOfferQueries, query, paginationModel]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const url = action === 'create'
        ? `${process.env.REACT_APP_API_URL}/api/v1/offer_queries`
        : `${process.env.REACT_APP_API_URL}/api/v1/offer_queries/${formData.id}`;

      const payload = {
        strategy: formData.strategy,
        active: formData.active,
        email_to: formData.emailTo,
        first_sent_not_before: formData.firstSentNotBefore ? new Date(formData.firstSentNotBefore).getTime() : undefined,
        last_sent_not_after: formData.lastSentNotAfter ? new Date(formData.lastSentNotAfter).getTime() : undefined,
        email_reply_to: formData.emailReplyTo,
        email_body_generation_prompt: formData.emailBodyGenerationPrompt,
        start_area: {
          country: formData.startCountry,
          city: formData.startCity,
          postal_code: formData.startPostalCode
        },
        end_area: {
          country: formData.endCountry,
          city: formData.endCity,
          postal_code: formData.endPostalCode
        },
        vehicle_properties: {
          body: formData.vehicleBodies,
          bodyProperty: formData.vehicleBodyProperties,
          equipment: formData.vehicleEquipments,
          loadSecuring: formData.vehicleLoadSecurities,
          swapBody: formData.vehicleSwappableBodyTypes,
          type: formData.vehicleTypes,
        }
      };
      await apiPost(url, payload);
      toast.success(`${t('Offer Query')} ${action === 'create' ? t('created') : t('updated')}!`);
      setFormData({} as FormData);
      setIsOpen(false);
      setHasValidationErrors(false);
      await fetchOfferQueries();
    } catch (error) {
      showErrorToast(error, t('Something went wrong!'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = (params: GridRowParams) => {
    const offerQuery = offerQueries.find(item => item.id === params.id);
    if (offerQuery) {
      setAction('update');
      setFormData({ ...offerQuery, id: Number(params.id) });
      setHasValidationErrors(false);
      setIsOpen(true);
    }
  };

  const renderStartCell = ({ value }: GridRenderCellParams) => (
    value ? `${value.country}, ${value.city}` : ''
  );

  const renderEndCell = ({ value }: GridRenderCellParams) => (
    value ? `${value.country}, ${value.city}` : ''
  );

  const renderActiveCell = ({ value }: GridRenderCellParams) => (
    value
      ? <Chip label={t('Active')} color='success' />
      : <Chip label={t('Not Active')} color='default' />
  );

  const renderStrategyCell = ({ value }: GridRenderCellParams) => (
    Object.values(OfferQueryStrategy)[value]
  );

  const handleDelete = async (id: string | number) => {
    const offerQuery = offerQueries.find(item => item.id === id);
    const ok = await confirmDelete(offerQuery?.startArea.country + ', ' + offerQuery?.startArea.city + ' -> ' + offerQuery?.endArea.country + ', ' + offerQuery?.endArea.city);
    if (!ok) return;
    try {
      await apiDelete(`${process.env.REACT_APP_API_URL}/api/v1/offer_query/${id}`);
      toast.success(t('Successfully deleted!'));
      await fetchOfferQueries();
    } catch (error) {
      showErrorToast(error, t('Email reminder delete failed!'));
    }
  };

  const renderActionCell = ({ id }: GridRenderCellParams) => (
    <DeleteIcon onClick={e => { e.stopPropagation(); handleDelete(id); }} />
  );

  const columns: GridColDef[] = [
    { field: 'id', headerName: t('ID'), width: 50 },
    { field: 'startArea', headerName: t('Start'), flex: 1, renderCell: renderStartCell },
    { field: 'endArea', headerName: t('End'), flex: 1, renderCell: renderEndCell },
    { field: 'strategy', headerName: t('Strategy'), flex: 1, renderCell: renderStrategyCell },
    { field: 'lastSentAt', headerName: t('Last sent at'), width: 150 },
    { field: 'active', headerName: t('Active'), width: 150, renderCell: renderActiveCell },
    { field: 'actions', headerName: t('Actions'), width: 150, renderCell: renderActionCell }
  ];

  return (
    <>
      <Header
        setIsOpen={setIsOpen}
        handleQuery={setQuery}
        rowCount={rowCount}
      />

      <Divider />

      <DataGrid
        rows={offerQueries}
        columns={columns}
        onRowClick={params => handleRowClick(params)}
        pagination
        paginationMode='server'
        disableRowSelectionOnClick
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 25]}
        rowCount={rowCount}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        autoHeight
        loading={loading}
        localeText={{
          noRowsLabel: t('No offer queries found'),
          noResultsOverlayLabel: t('No offer queries found'),
        }}
      />

      {/* modal */}
      <StandardDialog
        open={isOpen}
        onClose={() => { setIsOpen(false); setFormData({} as FormData); setAction('create'); setHasValidationErrors(false); }}
        title={action === 'create' ? t('Create Offer Query') : t('Update Offer Query')}
        maxWidth="lg"
        width={{ xs: '100%', sm: '95%', md: '900px', lg: '1000px' }}
        height={{ xs: '100vh', sm: '95vh', md: '88vh', lg: '85vh' }}
        primaryAction={{
          label: submitting
            ? (action === 'create' ? t('Creating...') : t('Updating...'))
            : (action === 'create' ? t('Create') : t('Update Offer Query')),
          onClick: async () => {
            if (formRef.current) {
              await formRef.current.submit();
            }
          },
          loading: submitting,
          disabled: submitting || hasValidationErrors
        }}
        secondaryAction={{
          label: t('Cancel'),
          onClick: () => { setIsOpen(false); setFormData({} as FormData); setAction('create'); setHasValidationErrors(false); }
        }}
        contentSx={{ p: { xs: 1.5, sm: 2, md: 2 } }}
      >
        <Form
          ref={formRef}
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          actionText=""
          onValidationChange={setHasValidationErrors}
        />
      </StandardDialog>
      {DeleteModal}
    </>
  );
};

export default OfferQueryPage;
